<div class="main_body">
  <app-page-header
    title="連絡帳" i18n-title="@@home.homeMentionList.title.main"
    subtitle="メンションの一覧を表示します。" i18n-subtitle="@@home.homeMentionList.subtitle.main"
    icon="dialog">
  </app-page-header>

  <div class="contents">
    <div class="contents_section_group">
      <div class="mention-layout">
        <!-- Left Panel: Mention List -->
        <div class="mention-layout__list">
          <div class="mention-layout__list-header">
            <!-- Search Pill -->
            <div
              class="search-pill"
              [class.expanded]="searchExpanded()"
              [class.has-value]="filterText().trim()"
              (click)="expandSearch()"
              role="search"
            >
              <mat-icon class="search-icon">search</mat-icon>
              @if (!searchExpanded() && !filterText().trim()) {
                <span class="search-placeholder" i18n="@@home.homeMentionList.search.placeholder">
                  キーワード検索
                </span>
              }
              <input
                #searchInput
                type="text"
                class="search-input"
                [value]="filterText()"
                (input)="applyFilter($event)"
                (blur)="onSearchBlur()"
                [attr.aria-label]="'キーワード検索'"
                i18n-aria-label="@@home.homeMentionList.search.ariaLabel"
              />
              @if (filterText().trim()) {
                <button
                  mat-icon-button
                  class="search-clear"
                  (click)="clearSearch($event)"
                  aria-label="検索をクリア"
                  i18n-aria-label="@@home.homeMentionList.search.clearAriaLabel"
                >
                  <mat-icon>close</mat-icon>
                </button>
              }
            </div>

            <!-- Filter Chips -->
            <div class="filter-chips">
              <mat-chip-listbox aria-label="フィルター">
                <mat-chip-option
                  [selected]="isNotReadOnly()"
                  (selectionChange)="onUnreadFilterChange($event)"
                  color="primary"
                >
                  <mat-icon matChipAvatar>mark_email_unread</mat-icon>
                  <span i18n="@@home.homeMentionList.chip.unreadOnly">未読のみ</span>
                  @if (unreadCount() > 0) {
                    <span class="unread-badge">{{ unreadCount() }}</span>
                  }
                </mat-chip-option>
              </mat-chip-listbox>
            </div>
          </div>

          <div class="mention-layout__list-body" role="listbox" aria-label="メンション一覧">
            @if (isLoading()) {
              @for (i of [1, 2, 3, 4, 5]; track i) {
                <div class="mention-skeleton">
                  <div class="mention-skeleton__avatar"></div>
                  <div class="mention-skeleton__lines">
                    <div class="mention-skeleton__line mention-skeleton__line--medium"></div>
                    <div class="mention-skeleton__line mention-skeleton__line--short"></div>
                    <div class="mention-skeleton__line mention-skeleton__line--long"></div>
                  </div>
                </div>
              }
            } @else {
              @for (mention of filteredMentions(); track mention.id) {
                <div
                  class="mention-item"
                  [class.mention-item--active]="isSelected(mention)"
                  [class.mention-item--unread]="!mention.data.isRead"
                  (click)="selectMention(mention)"
                  (keydown.enter)="selectMention(mention)"
                  role="option"
                  [attr.aria-selected]="isSelected(mention)"
                  tabindex="0"
                >
                  <span class="mention-item__avatar">
                    {{ getInitials(mention) }}
                  </span>
                  <div class="mention-item__content">
                    <div class="mention-item__header">
                      <span class="mention-item__name">{{ getSenderName(mention) }}</span>
                      <span class="mention-item__time">
                        {{ mention.commonInfo.lastModifiedAt | date: 'MM/dd HH:mm' }}
                      </span>
                    </div>
                    <div class="mention-item__section">{{ mention.data.selectedSectionName }}</div>
                    <div class="mention-item__preview">
                      {{ genMentionPlainText(mention.data.mentionedContent) | slice: 0 : 40 }}
                    </div>
                  </div>
                  @if (!mention.data.isRead) {
                    <span class="mention-item__unread-dot" aria-hidden="true"></span>
                    <span class="visually-hidden" i18n="@@home.homeMentionList.unread">未読</span>
                  }
                </div>
              } @empty {
                <div class="mention-list__empty">
                  <mat-icon>mark_email_read</mat-icon>
                  <span i18n="@@home.homeMentionList.list.empty">メンションはありません</span>
                </div>
              }
            }
          </div>
        </div>

        <!-- Right Panel: Mention Detail -->
        <div class="mention-layout__detail" role="region" aria-label="メンション詳細">
          @if (selectedMention(); as mention) {
            <div class="mention-detail__header">
              <span class="mention-detail__avatar">
                {{ getInitials(mention) }}
              </span>
              <div class="mention-detail__sender-info">
                <div class="mention-detail__sender-name">{{ getSenderName(mention) }}</div>
                <div class="mention-detail__sender-meta">
                  <span>{{ mention.data.selectedSectionName }}</span>
                  <span class="mention-detail__meta-separator">|</span>
                  <span>{{ mention.commonInfo.lastModifiedAt | date: 'yyyy/MM/dd HH:mm' }}</span>
                  @if (!mention.data.isRead) {
                    <span class="badge badge--info" i18n="@@home.homeMentionList.badge.unread">未読</span>
                  } @else {
                    <span class="badge badge--success" i18n="@@home.homeMentionList.badge.read">既読</span>
                  }
                </div>
              </div>
            </div>

            <div class="mention-detail__body">
              <div class="mention-detail__bubble">
                {{ genMentionPlainText(mention.data.mentionedContent) }}
              </div>
            </div>

            <div class="mention-detail__actions">
              <button
                mat-flat-button
                color="primary"
                (click)="navigateToMention(mention)"
              >
                <mat-icon>open_in_new</mat-icon>
                <span i18n="@@home.homeMentionList.detail.goToDialog">連絡帳に移動</span>
              </button>
            </div>
          } @else {
            <div class="mention-detail__empty">
              <mat-icon>mark_email_read</mat-icon>
              <div class="mention-detail__empty-title"
                   i18n="@@home.homeMentionList.detail.emptyState.title">
                メンションを選択してください
              </div>
              <div class="mention-detail__empty-description"
                   i18n="@@home.homeMentionList.detail.emptyState.description">
                左のリストからメンションを選択すると、詳細が表示されます
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
</div>
